define('views/modules/graph/graph',[
    // Libs
    'underscore', 'backbone', 'mustache', 'moment', 'utils',
    // services
    'services/viewport',
    'services/i18n',
    'services/user',
    'services/unit-converter',
    // collections & models
    'collections/calendar/personal-data/personal-data-types',
    'models/graph/graph',
    // views
    'views/parts/chart/chart-line',
    'views/parts/form/select',
    'views/parts/form/choices',
    // mixins
    'mixins/graph/graph-mixin',
    // templates
    'text!templates/modules/graph/graph.html',

], function(
    // Libs
    _, Backbone, Mustache, moment, U,
    // services
    Viewport,
    i18n,
    User,
    UnitConverter,
    // collections & models
    CPersonalDataTypes,
    MGraph,
    // views
    VChartLine,
    VSelect,
    VChoices,
    // mixins
    GraphMixin,
    // templates
    TGraph
) {
    var FREQUENCY = {
        "daily": "day",
        "weekly": "week",
        "monthly": "month",
    };

    // TODO: Fetch data with custom data when Max will have done it
    // TODO: Datepicker icon fix

    var VGraph = Backbone.View.extend(_.extend({
        template: TGraph,
        title: function() {
            return Mustache.render('graph:Graphs', Mustache.getContext());
        },
        subtitle: 'graph:All your data at the same place',

        events: {
            "click .calendar-date-from-toggle": "toggleDateFrom", // calendar icon fix
            "click .calendar-date-to-toggle": "toggleDateTo",
            "hide .calendar-date-from": "onDateFromPicked",
            "hide .calendar-date-to": "onDateToPicked",
        },

        // abstracting selectors out of the view logic
        regions: {
            chart: ".graph-chart",
            daterange: ".input-daterange",
            from: ".calendar-date-from",
            to: ".calendar-date-to",
            series: ".series",
            frequencies: ".frequencies",
        },

        initialize: function(opt) {

            var model = this.model = new MGraph();

            _.extend(this, {
                config: model.getDefaultConfig(),
                personalData: new CPersonalDataTypes(),
                profile: opt.model
            });

            var collection = this.collection = model.getDefaultSeries(),
                view = this.view = {
                    chart: new VChartLine({ data: this.data }),
                    series: new VSelect({
                        labelposition: "top",
                        option: { label: 'label', value: 'value' },
                        attribute: 'type',
                        addClass: 'btn-sm',
                        model: this.config,
                        collection: collection,
                        onChange: this.onSeriesChange,
                        context: this,
                    }),
                    frequencies: new VChoices({
                        labelposition: "top",
                        choices: model.getFrequencies(),
                        model: this.config,
                        attribute: 'frequency',
                        radio: true,
                        styleButton: true,
                        onChange: this.onFrequencyChange,
                        context: this,
                    }),
                };

            this.options = new Backbone.Model(_.extend({
                listDisplay: false,
                manuelListDisplay: false,
                forcedListDisplay: false,
                weeks: 4
            }, this.defaults, opt));

            // Setup Datepicker
            this.datePickerOptions = {
                autoclose: true,
                todayHighlight: true,
                language: i18n.language.substring(0, 2)
            };

            // default dates
            var dates = this.dates = new Backbone.Model({
                from: moment().subtract(this.options.get('weeks') * 7, 'days'),
                to: moment().add(1, 'week')
            });

            this.config.set({
                startDate: dates.get('from').format('YYYY-MM-DD'),
                endDate: dates.get('to').format('YYYY-MM-DD')
            });

            this.initGraph();

            this.listenTo(this.config, "change", this.onConfigChange)
                .listenTo(this.personalData, "sync", this.onPersonalDataSync)
                .listenTo(this.profile, { 'sync': this.onProfileSync });
        },

        render: function() {
            this.$el.empty().append(Mustache.render(this.template, Mustache.getContext()));

            this.generateZones();
            var view = this.view,
                zone = this.zone;

            this.assign(view.series, zone.$series)
                .assign(view.frequencies, zone.$frequencies)
                .assign(view.chart, zone.$chart);

            this.initDatepicker();

            this.personalData.fetch();
            this.loadGraph();

            return this;
        },

        initGraph: function() {
            var config = this.config,
                seriesView = this.view.series;

            this.model.setRequest({
                type: config.get('type'),
                frequency: config.get('frequency'),
                startDate: config.get('startDate'),
                endDate: config.get('endDate'),
                done: config.get('done')
            });

            this.graphTitle = seriesView.getLabel();
            this.datasetUnit = this.getDatasetUnit(seriesView.getValue());
            this.displayUnit = this.getDisplayUnit(seriesView.getValue(), User, this.datasetUnit);
        },

        initDatepicker: function() {
            var zone = this.zone,
                dates = this.dates;
            zone.$daterange.datepicker(this.datePickerOptions);
            zone.$from.datepicker("setDate", dates.get('from').toDate());
            zone.$to.datepicker("setDate", dates.get('to').toDate());
        },

        renderGraph: function() {
            this.chartLabels = [];
            this.chartData = [];

            _.each(this.model.attributes, function(value, key) {
                //Conversion of units
                if (this.datasetUnit !== this.displayUnit && value > 0)
                {
                    this.displayUnit = this.datasetUnit;
                    value = UnitConverter.convert(value, this.datasetUnit, this.displayUnit);
                }

                this.chartData.push(value);
                this.chartLabels.push(key);
            }, this);

            var xAxisUnit = this.xAxisUnit = this.getXAxisUnit(),
                frequency = this.config.get('frequency');

            this.min = this.getMinDateByFrequency(this.dates.get('from'), frequency);
            this.max = this.getMinDateByFrequency(this.dates.get('to'), frequency);

            this.view.chart.updateChart({
                labels: this.chartLabels,
                data: this.chartData,
                unit: xAxisUnit,
                min: this.min,
                max: this.max,
                xAxisUnit: i18n.t('graph:' + xAxisUnit),
                datasetUnit: i18n.t(this.displayUnit),
                unitStepSize: this.getUnitStepSize(),
                yAxisMaxValue: this.getYAxisMinMaxValue(this.chartData, this.view.series.getValue()).max,
                yAxisMinValue: this.getYAxisMinMaxValue(this.chartData, this.view.series.getValue()).min,
            });
        },

        getYAxisMinMaxValue: function(dataset, graphType) {

          var val = { min: 0, max: 0 };
          var dataMax = 0, dataMin = 0;
          //get the max value from the dataset to display
          if (dataset.length > 0 && Math.max.apply(Math, dataset) !== Infinity && Math.max.apply(Math, dataset) !== -Infinity)
              dataMax = Math.max.apply(Math, dataset);
          //get the min value from the dataset to display
          if (dataset.length > 0 && Math.min.apply(Math, dataset) !== Infinity && Math.min.apply(Math, dataset) !== -Infinity)
              dataMin = Math.min.apply(Math, dataset);
          //
          // if (graphType.indexOf("custom") > -1)
          //     graphType = "custom";

          switch (graphType) {
            case "feedback_level":
                val.max = 11;
                break;
            case "duration":
                val.max = Math.round(dataMax * 1.15);
                break;
            case "calories":
                val.max = Math.round(dataMax * 1.15);
                break;
            case "distance":
                val.max = Math.round(dataMax * 1.15);
                break;
            default:
                //for the type "weight", "waistline" and "Custom", the min can change too!

                var diffMinMax = 0, axeMax = 0, axeMin = 0, valToAddToMinAndMax = 0;
                //Diff between max and min value in the dataset
                diffMinMax = dataMax - dataMin;
                //Compute the offset to add or substract to max and min value in dataset
                valToAddToMinAndMax = diffMinMax * 0.15;
                //Zero management
                if (valToAddToMinAndMax < 1) valToAddToMinAndMax = 1;
                //Graph max and min axis display
                axeMax = Math.round(dataMax + valToAddToMinAndMax);
                axeMin = Math.round(dataMin - valToAddToMinAndMax);
                //Zero management
                if (axeMin < 0 && dataMin >= 0) axeMin = 0;
                val.max = axeMax;
                val.min = axeMin;
                break;
          }

          if (val.max === 0) val.max = 1;

          return val;
        },

        getDisplayUnit: function(type, usr, dbUnit) {
          var units = usr.get('units');
          if (type.indexOf('custom') !== -1)
              return dbUnit;

          switch (type) {
              case 'waistline':
                  return usr.get('units').length;
              case 'weight':
                  return usr.get('units').weight;
              case 'distance':
                  return usr.get('units').distance;
              default:
                  return dbUnit;
          }

        },
        ////////////////////
        // dates handling //
        ////////////////////
        toggleDateFrom: function(e) {
            this.zone.$from.datepicker('show');
        },
        toggleDateTo: function(e) {
            this.zone.$to.datepicker('show');
        },

        setFrom: function(newFrom) {
            var fromDate = moment(newFrom),
                dates = this.dates;
            dates.set({ from: fromDate });
            // prevent impossible range
            if (dates.get('to').isBefore(fromDate, 'day')) {
                this.setTo(fromDate);
            }
            this.zone.$from.datepicker("setDate", fromDate.toDate());
        },

        setTo: function(newTo) {
            var toDate = moment(newTo),
                dates = this.dates;
            dates.set({ to: toDate });
            // prevent impossible range
            if (dates.get('from').isAfter(dates.get('to'), 'day')) {
                this.setFrom(toDate);
            }

            this.zone.$to.datepicker("setDate", toDate.toDate());
        },

        getXAxisUnit: function() {
            return FREQUENCY[this.config.get('frequency')];
        },

        getUnitStepSize: function() {
            var unit = this.getXAxisUnit(),
                unitStepSize = 1,
                minDate = this.dates.get('from'),
                daysBetween = moment(this.max).diff(minDate, 'days'),
                weeksBetween = moment(this.max).diff(minDate, 'weeks'),
                isMobile = Viewport.isMobile();

            if (unit === 'day') {
                unitStepSize = this.getDayStepSize(daysBetween, isMobile);
            } else if (unit === 'week' && weeksBetween > 20) {
                unitStepSize = 2;
            }

            return unitStepSize;
        },

        getDayStepSize: function(daysBetween, isMobile) {
          var unitStepSize = 0;
          if (daysBetween > 20 && daysBetween <= 50) {
              unitStepSize = isMobile ? 3 : 2;
          } else if (daysBetween > 50 && daysBetween <= 90) {
              unitStepSize = isMobile ? 5 : 3;
          } else if (daysBetween > 90 && daysBetween <= 120) {
              unitStepSize = isMobile ? 8 : 5;
          } else if (daysBetween > 120) {
              unitStepSize = isMobile ? Math.round(daysBetween / 24) : Math.round(daysBetween / 20);
          }
          return unitStepSize;
        },

        ////////////
        // events //
        ////////////

        /** only triggers when the from date is picked from the UI by the user */
        onDateFromPicked: function(e) {
            var dates = this.dates,
                oldDate = dates.get('from');
            this.setFrom(e.date);

            if (!dates.get('from').isSame(oldDate, 'day')) {
                this.config.set('startDate', dates.get('from').format('YYYY-MM-DD'));
            }
        },

        /** only triggers when the to date is picked from the UI by the user */
        onDateToPicked: function(e) {
            var dates = this.dates,
                oldDate = dates.get('to');
            this.setTo(e.date);

            if (!dates.get('to').isSame(oldDate, 'day')) {
                this.config.set('endDate', dates.get('to').format('YYYY-MM-DD'));
            }
        },

        onFrequencyChange: function(model, value) {
            this.config.set('frequency', value);
        },

        onConfigChange: function(model, options) {
            this.model.setRequest({
                type: model.get('type'),
                frequency: model.get('frequency'),
                startDate: model.get('startDate'),
                endDate: model.get('endDate'),
                done: true
            });

            this.loadGraph();
        },

        onSeriesChange: function(model, value) {
            this.graphTitle = this.view.series.getLabel();
            this.datasetUnit = this.getDatasetUnit(value);
            this.displayUnit = this.getDisplayUnit(value, User, this.datasetUnit);
        },

    }, GraphMixin));

    return VGraph;

});

